if (!customElements.get('testimonial-slider')) {
  const DESKTOP_QUERY = '(min-width: 750px)';
  const SWIPE_THRESHOLD = 40;

  class TestimonialSlider extends HTMLElement {
    constructor() {
      super();
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.disconnectedCallback();
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.desktopQuery.addEventListener('change', this.onViewportChange);
      this.addEventListener('click', this.onClick, true);
      this.addEventListener('keydown', this.onKeyDown);

      if (typeof Swiper === 'undefined') {
        this.onLoad = () => {
          if (this.isConnected) this.init();
        };
        window.addEventListener('load', this.onLoad, { once: true });
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      this.endGesture();
      this.desktopQuery?.removeEventListener('change', this.onViewportChange);
      this.removeEventListener('click', this.onClick, true);
      this.removeEventListener('keydown', this.onKeyDown);
      if (this.onLoad) window.removeEventListener('load', this.onLoad);
      this.destroySlider();
    }

    get swiperElement() {
      return this.querySelector('.swiper');
    }

    get slideElements() {
      return [...this.querySelectorAll('.swiper-wrapper > .swiper-slide')];
    }

    currentMode() {
      const count = this.slideElements.length;
      if (count <= 1) return 'single';
      if (this.desktopQuery.matches && count < 3) return 'pair';
      return 'carousel';
    }

    onViewportChange() {
      const nextKey = this.layoutKey();
      if (nextKey !== this.layout) {
        const index = this.swiper && !this.swiper.destroyed ? this.swiper.realIndex : 0;
        this.init(index);
        return;
      }

      if (this.swiper && !this.swiper.destroyed) this.swiper.update();
    }

    layoutKey() {
      return `${this.currentMode()}-${this.desktopQuery.matches ? 'desktop' : 'mobile'}`;
    }

    init(startIndex = 0) {
      if (!this.isConnected) return;
      this.endGesture();
      this.destroySlider();
      this.querySelectorAll('[data-testimonial-clone]').forEach((node) => node.remove());

      const element = this.swiperElement;
      if (!element || typeof Swiper === 'undefined') return;

      const count = this.slideElements.length;
      if (count === 0) return;

      this.mode = this.currentMode();
      this.layout = this.layoutKey();
      this.classList.remove(
        'testimonial-slider--carousel',
        'testimonial-slider--pair',
        'testimonial-slider--single'
      );
      this.classList.add(`testimonial-slider--${this.mode}`);

      if (this.mode === 'carousel' && this.desktopQuery.matches && count === 3) {
        this.duplicateSlidesForLoop();
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const speed = reduceMotion ? 0 : 450;
      const pagination = this.querySelector('.swiper-pagination');
      const index = ((startIndex % count) + count) % count;

      const options = {
        slidesPerGroup: 1,
        speed,
        watchOverflow: true,
        initialSlide: index,
      };

      if (this.mode === 'single') {
        Object.assign(options, {
          slidesPerView: 1,
          centeredSlides: true,
          loop: false,
          allowTouchMove: false,
          simulateTouch: false,
        });
      } else if (this.mode === 'pair') {
        Object.assign(options, {
          slidesPerView: 2,
          centeredSlides: false,
          spaceBetween: 24,
          loop: false,
          allowTouchMove: false,
          simulateTouch: false,
        });
      } else if (this.desktopQuery.matches) {
        Object.assign(options, {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: 0,
          loop: true,
          allowTouchMove: false,
          simulateTouch: false,
        });
      } else {
        Object.assign(options, {
          slidesPerView: 1,
          centeredSlides: true,
          spaceBetween: 20,
          loop: true,
          allowTouchMove: false,
          simulateTouch: false,
          watchOverflow: false,
          pagination: pagination
            ? {
                el: pagination,
                type: 'progressbar',
                clickable: false,
              }
            : undefined,
        });
      }

      this.swiper = new Swiper(element, options);

      if (this.mode === 'carousel') {
        this.swiper.slideToLoop(index, 0);
        element.addEventListener('pointerdown', this.onPointerDown);
        element.addEventListener('dragstart', this.onDragStart);
      }
    }

    destroySlider() {
      this.swiperElement?.removeEventListener('pointerdown', this.onPointerDown);
      this.swiperElement?.removeEventListener('dragstart', this.onDragStart);
      if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
      this.swiper = null;
    }

    duplicateSlidesForLoop() {
      const wrapper = this.querySelector('.swiper-wrapper');
      this.slideElements.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-testimonial-clone', '');
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
          node.removeAttribute('data-shopify-editor-block');
        });
        wrapper.append(clone);
      });
    }

    slideLimit() {
      const size = this.swiper.slidesSizesGrid?.[this.swiper.activeIndex] || 0;
      const gap = Number(this.swiper.params.spaceBetween) || 0;
      return (size || this.swiperElement.clientWidth || 0) + gap;
    }

    onDragStart(event) {
      if (this.mode !== 'carousel') return;
      event.preventDefault();
    }

    onPointerDown(event) {
      this.suppressClick = false;
      if (this.mode !== 'carousel' || !this.swiper || event.button > 0) return;
      if (this.gesture) this.endGesture();

      this.gesture = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        translate: this.swiper.translate,
        axis: null,
        dx: 0,
        prepared: null,
      };

      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.gesture || event.pointerId !== this.gesture.id || !this.swiper) return;

      const dx = event.clientX - this.gesture.x;
      const dy = event.clientY - this.gesture.y;

      if (!this.gesture.axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        this.gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this.gesture.axis === 'y') {
          this.endGesture();
          return;
        }
        this.classList.add('is-dragging');
        this.swiper.setTransition(0);
      }

      if (this.gesture.axis !== 'x') return;

      this.prepareLoop(dx < 0 ? 'next' : 'prev');

      const limit = this.slideLimit();
      const clamped = Math.max(-limit, Math.min(limit, dx));
      this.gesture.dx = dx;
      this.swiper.setTransition(0);
      this.swiper.setTranslate(this.gesture.translate + clamped);
    }

    prepareLoop(direction) {
      const swiper = this.swiper;
      if (!this.gesture || !swiper?.params.loop || this.gesture.prepared === direction) return;

      const before = swiper.translate;
      swiper.loopFix({ direction });
      this.gesture.translate += swiper.translate - before;
      this.gesture.prepared = direction;
      swiper.setTransition(0);
    }

    onPointerUp(event) {
      if (!this.gesture || (event && event.pointerId !== this.gesture.id)) return;

      const { dx, axis } = this.gesture;
      const swiper = this.swiper;
      const canceled = event?.type === 'pointercancel';
      this.endGesture();

      if (canceled || !swiper || swiper.destroyed || axis !== 'x') {
        this.suppressClick = false;
        return;
      }

      const speed = swiper.params.speed;
      if (Math.abs(dx) < SWIPE_THRESHOLD) {
        this.suppressClick = false;
        swiper.slideTo(swiper.activeIndex, speed);
        return;
      }

      this.suppressClick = true;
      swiper.slideTo(swiper.activeIndex + (dx < 0 ? 1 : -1), speed);
    }

    endGesture() {
      this.gesture = null;
      this.classList.remove('is-dragging');
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerUp);
    }

    onClick(event) {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    onKeyDown(event) {
      this.suppressClick = false;
      if (this.mode !== 'carousel' || !this.swiper || this.swiper.destroyed) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
    }

    step(direction) {
      const swiper = this.swiper;
      if (!swiper || swiper.destroyed) return;

      let shifted = false;
      if (swiper.params.loop) {
        const before = swiper.translate;
        swiper.loopFix({ direction: direction > 0 ? 'next' : 'prev' });
        shifted = swiper.translate !== before;
      }

      const go = () => {
        if (!swiper.destroyed) swiper.slideTo(swiper.activeIndex + direction, swiper.params.speed);
      };

      if (shifted) requestAnimationFrame(() => requestAnimationFrame(go));
      else go();
    }
  }

  customElements.define('testimonial-slider', TestimonialSlider);
}
